import React, { useEffect, useState } from 'react';
import './Turntable.css';

// Images are served from public/turntable folder
const TURNTABLE_IMAGES = {
  turntable: '/turntable/pioneertt.png',
  mat: '/turntable/mat.png',
  needle: '/turntable/pioneerNeedle.png',
  jukeboxBase: '/turntable/jukeboxBase.jpg',
  defaultAlbum: '/turntable/beatlesAlbum.png',
};

// Default positioning values (percentages)
const DEFAULT_POSITIONS = {
  jukebox: { width: 100, left: 50, top: 50 },
  assembly: { width: 50, top: 50, left: 50 },
  mat: { width: 52, left: 5, top: 50 },
  needle: { width: 22, right: 8, top: -5 },
  record: { width: 96, left: 50, top: 50 },
  spindle: { width: 4, left: 50, top: 50 },
  controls: { top: 20, left: 50 }
};

// Config file path
const CONFIG_PATH = '/turntable/config.json';
const SAVE_SERVER_URL = 'http://localhost:3001/save-config';

const Turntable = ({ 
  isPlaying = false, 
  currentTrack = null,
  albumCover = null,
  onPlayPause = () => {},
  onNext = () => {},
  onPrevious = () => {},
  onRestart = () => {},
  onSkipForward = () => {},
  onSkipBackward = () => {},
  showJukebox = false,
  queueLength = 0,
  currentIndex = 0,
  debugMode = false // Enable calibration GUI
}) => {
  const [needleState, setNeedleState] = useState('rest'); // 'rest', 'cueing', 'playing', 'paused', 'resetting'
  const [isSpinning, setIsSpinning] = useState(false);
  const [hasStarted, setHasStarted] = useState(false); // Track if playback has started
  const [showCalibration, setShowCalibration] = useState(debugMode);
  const [savedIndicator, setSavedIndicator] = useState(false);
  const [configLoaded, setConfigLoaded] = useState(false);
  
  // Position state for calibration
  const [positions, setPositions] = useState(DEFAULT_POSITIONS);

  // Load config from JSON file on mount
  useEffect(() => {
    fetch(CONFIG_PATH)
      .then(response => response.json())
      .then(data => {
        // Merge with defaults to handle any missing properties
        // Support both "album" (old) and "record" (new) keys
        const recordData = data.record || data.album || {};
        setPositions({
          ...DEFAULT_POSITIONS,
          ...data,
          jukebox: { ...DEFAULT_POSITIONS.jukebox, ...data.jukebox },
          assembly: { ...DEFAULT_POSITIONS.assembly, ...data.assembly },
          mat: { ...DEFAULT_POSITIONS.mat, ...data.mat },
          needle: { ...DEFAULT_POSITIONS.needle, ...data.needle },
          record: { ...DEFAULT_POSITIONS.record, ...recordData },
          spindle: { ...DEFAULT_POSITIONS.spindle, ...data.spindle },
          controls: { ...DEFAULT_POSITIONS.controls, ...data.controls }
        });
        setConfigLoaded(true);
        console.log('Turntable config loaded from', CONFIG_PATH);
      })
      .catch(err => {
        console.warn('Could not load turntable config, using defaults:', err);
        setConfigLoaded(true);
      });
  }, []);

  // Update a specific position value
  const updatePosition = (element, property, value) => {
    setPositions(prev => ({
      ...prev,
      [element]: {
        ...prev[element],
        [property]: parseFloat(value)
      }
    }));
  };


  // Save positions to config file via save server
  const savePositions = async () => {
    try {
      const response = await fetch(SAVE_SERVER_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(positions)
      });
      
      const result = await response.json();
      
      if (result.success) {
        setSavedIndicator(true);
        setTimeout(() => setSavedIndicator(false), 3000);
        console.log('✅ Config saved successfully!');
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      console.error('Save failed:', error);
      // Fallback to clipboard if server not running
      const json = JSON.stringify(positions, null, 2);
      navigator.clipboard.writeText(json).then(() => {
        alert('Save server not running!\n\nJSON copied to clipboard instead.\nPaste into: web/public/turntable/config.json\n\nOr run: node save-server.js');
      });
    }
  };

  // Reset to defaults
  const resetPositions = () => {
    setPositions(DEFAULT_POSITIONS);
  };


  // Handle play state changes - only react to isPlaying changes
  useEffect(() => {
    let cueTimeout;

    if (isPlaying) {
      setIsSpinning(true);
      
      if (!hasStarted || needleState === 'rest') {
        // First play or after full reset - cue the needle
        setNeedleState('cueing');
        setHasStarted(true);

        // After cue animation (3s), switch to playing state
        cueTimeout = setTimeout(() => {
          setNeedleState('playing');
        }, 3000);
      } else if (needleState === 'paused') {
        // Resume from pause - drop needle back down
        setNeedleState('playing');
      }
    } else {
      // Pause - lift needle but keep position
      if (hasStarted && needleState !== 'rest') {
        setNeedleState('paused');
        setIsSpinning(false);
      }
    }

    return () => {
      clearTimeout(cueTimeout);
    };
  }, [isPlaying]);

  // Get needle animation class based on state
  const getNeedleClass = () => {
    switch (needleState) {
      case 'cueing':
        return 'needle-cue';
      case 'playing':
        return 'needle-play';
      case 'paused':
        return 'needle-paused';
      case 'resetting':
        return 'needle-reset';
      default:
        return '';
    }
  };

  // Handle restart - reset to beginning
  const handleRestart = () => {
    setHasStarted(false);
    setNeedleState('rest');
    setIsSpinning(false);
    onRestart();
  };

  // Default album art if none provided
  const displayAlbumCover = albumCover || currentTrack?.album?.coverUrl || TURNTABLE_IMAGES.defaultAlbum;

  // Dynamic styles based on calibration
  const jukeboxStyle = {
    width: `${positions.jukebox.width}%`,
    left: `${positions.jukebox.left}%`,
    top: `${positions.jukebox.top}%`,
    transform: 'translate(-50%, -50%)',
    position: 'absolute',
    height: 'auto'
  };

  const assemblyStyle = showJukebox ? {
    width: `${positions.assembly.width}%`,
    top: `${positions.assembly.top}%`,
    left: `${positions.assembly.left}%`,
    transform: 'translate(-50%, -50%)'
  } : {};

  const matStyle = {
    width: `${positions.mat.width}%`,
    left: `${positions.mat.left}%`,
    top: `${positions.mat.top}%`
  };

  const needleStyle = {
    width: `${positions.needle.width}%`,
    right: `${positions.needle.right}%`,
    top: `${positions.needle.top}%`
  };

  const recordStyle = {
    width: `${positions.record.width}%`,
    left: `${positions.record.left}%`,
    top: `${positions.record.top}%`
  };

  const spindleStyle = {
    width: `${positions.spindle.width}%`,
    left: `${positions.spindle.left}%`,
    top: `${positions.spindle.top}%`
  };

  const controlsStyle = showJukebox ? {
    top: `${positions.controls.top}px`,
    left: `${positions.controls.left}%`,
    transform: 'translateX(-50%)'
  } : {};

  return (
    <div className={`turntable-container ${showJukebox ? 'with-jukebox' : ''}`}>
      {/* Calibration Toggle Button */}
      <button 
        className="calibration-toggle"
        onClick={() => setShowCalibration(!showCalibration)}
        title="Toggle Calibration Mode"
      >
        ⚙️
      </button>

      {/* Calibration Panel */}
      {showCalibration && (
        <div className="calibration-panel">
          <h3>🎛️ Turntable Calibration</h3>
          <div className="viewport-info">
            Viewport: {window.innerWidth} x {window.innerHeight}
          </div>
          
          {/* Jukebox Controls */}
          <div className="calibration-section">
            <h4>🎰 Jukebox Image</h4>
            <label>
              Width: {positions.jukebox.width}%
              <input type="range" min="50" max="200" step="1" 
                value={positions.jukebox.width} 
                onChange={(e) => updatePosition('jukebox', 'width', e.target.value)} />
            </label>
            <label>
              Left (X): {positions.jukebox.left}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.jukebox.left} 
                onChange={(e) => updatePosition('jukebox', 'left', e.target.value)} />
            </label>
            <label>
              Top (Y): {positions.jukebox.top}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.jukebox.top} 
                onChange={(e) => updatePosition('jukebox', 'top', e.target.value)} />
            </label>
          </div>

          {/* Assembly Controls */}
          <div className="calibration-section">
            <h4>📦 Assembly Container</h4>
            <label>
              Width: {positions.assembly.width}%
              <input type="range" min="10" max="100" step="1" 
                value={positions.assembly.width} 
                onChange={(e) => updatePosition('assembly', 'width', e.target.value)} />
            </label>
            <label>
              Top: {positions.assembly.top}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.assembly.top} 
                onChange={(e) => updatePosition('assembly', 'top', e.target.value)} />
            </label>
            <label>
              Left: {positions.assembly.left}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.assembly.left} 
                onChange={(e) => updatePosition('assembly', 'left', e.target.value)} />
            </label>
          </div>

          {/* Mat Controls */}
          <div className="calibration-section">
            <h4>💿 Platter Mat</h4>
            <label>
              Width: {positions.mat.width}%
              <input type="range" min="10" max="80" step="1" 
                value={positions.mat.width} 
                onChange={(e) => updatePosition('mat', 'width', e.target.value)} />
            </label>
            <label>
              Left: {positions.mat.left}%
              <input type="range" min="-20" max="50" step="1" 
                value={positions.mat.left} 
                onChange={(e) => updatePosition('mat', 'left', e.target.value)} />
            </label>
            <label>
              Top: {positions.mat.top}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.mat.top} 
                onChange={(e) => updatePosition('mat', 'top', e.target.value)} />
            </label>
          </div>

          {/* Needle Controls */}
          <div className="calibration-section">
            <h4>🎵 Needle/Tonearm</h4>
            <label>
              Width: {positions.needle.width}%
              <input type="range" min="5" max="40" step="1" 
                value={positions.needle.width} 
                onChange={(e) => updatePosition('needle', 'width', e.target.value)} />
            </label>
            <label>
              Right: {positions.needle.right}%
              <input type="range" min="-20" max="50" step="1" 
                value={positions.needle.right} 
                onChange={(e) => updatePosition('needle', 'right', e.target.value)} />
            </label>
            <label>
              Top: {positions.needle.top}%
              <input type="range" min="-50" max="50" step="1" 
                value={positions.needle.top} 
                onChange={(e) => updatePosition('needle', 'top', e.target.value)} />
            </label>
          </div>

          {/* Record Controls */}
          <div className="calibration-section">
            <h4>💿 Record</h4>
            <label>
              Width: {positions.record.width}%
              <input type="range" min="50" max="100" step="1" 
                value={positions.record.width} 
                onChange={(e) => updatePosition('record', 'width', e.target.value)} />
            </label>
            <label>
              Left (X): {positions.record.left}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.record.left} 
                onChange={(e) => updatePosition('record', 'left', e.target.value)} />
            </label>
            <label>
              Top (Y): {positions.record.top}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.record.top} 
                onChange={(e) => updatePosition('record', 'top', e.target.value)} />
            </label>
          </div>

          {/* Spindle Controls */}
          <div className="calibration-section">
            <h4>⚫ Center Spindle</h4>
            <label>
              Width: {positions.spindle.width}%
              <input type="range" min="1" max="15" step="0.5" 
                value={positions.spindle.width} 
                onChange={(e) => updatePosition('spindle', 'width', e.target.value)} />
            </label>
            <label>
              Left (X): {positions.spindle.left}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.spindle.left} 
                onChange={(e) => updatePosition('spindle', 'left', e.target.value)} />
            </label>
            <label>
              Top (Y): {positions.spindle.top}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.spindle.top} 
                onChange={(e) => updatePosition('spindle', 'top', e.target.value)} />
            </label>
          </div>

          {/* Media Controls Position */}
          <div className="calibration-section">
            <h4>🎮 Media Controls</h4>
            <label>
              Top: {positions.controls.top}px
              <input type="range" min="0" max="200" step="5" 
                value={positions.controls.top} 
                onChange={(e) => updatePosition('controls', 'top', e.target.value)} />
            </label>
            <label>
              Left: {positions.controls.left}%
              <input type="range" min="0" max="100" step="1" 
                value={positions.controls.left} 
                onChange={(e) => updatePosition('controls', 'left', e.target.value)} />
            </label>
          </div>

          {/* Action Buttons */}
          <div className="calibration-actions">
            <button 
              onClick={savePositions} 
              className={savedIndicator ? 'saved' : ''}
              title="Copy settings to clipboard, then paste into config.json"
            >
              {savedIndicator ? '✅ Copied to Clipboard!' : '💾 Save Settings'}
            </button>
            <button onClick={resetPositions} title="Reset to defaults">🔄 Reset</button>
          </div>
          
          {/* Status */}
          <div className="storage-status">
            {savedIndicator 
              ? '✅ Saved to config.json!' 
              : (configLoaded ? '✅ Config loaded' : '⏳ Loading...')}
          </div>
        </div>
      )}

      {/* Jukebox background (optional) */}
      {showJukebox && (
        <div className="jukebox-background">
          <img 
            src={TURNTABLE_IMAGES.jukeboxBase} 
            alt="Jukebox" 
            className="jukebox-image"
            style={jukeboxStyle}
          />
        </div>
      )}

      {/* Turntable assembly */}
      <div 
        className={`turntable-assembly ${isPlaying ? 'is-playing' : ''} ${showJukebox ? 'on-jukebox' : ''}`}
        style={assemblyStyle}
      >
        {/* Layer 1: Pioneer turntable base */}
        <img 
          src={TURNTABLE_IMAGES.turntable} 
          alt="Turntable" 
          className="turntable-base"
        />

        {/* Layer 2: Platter mat (spins) */}
        <div 
          className={`platter-mat ${isSpinning ? 'spinning' : ''}`}
          style={matStyle}
        >
          <img 
            src={TURNTABLE_IMAGES.mat} 
            alt="Platter Mat" 
            className="mat-image"
          />

          {/* Layer 3: Record/Album cover (spins with mat) */}
          {displayAlbumCover && (
            <div className="album-label" style={recordStyle}>
              <img 
                src={displayAlbumCover} 
                alt="Album" 
                className="album-label-image"
              />
            </div>
          )}

          {/* Center spindle */}
          <div className="center-spindle" style={spindleStyle}></div>
        </div>

        {/* Layer 4: Tonearm/Needle (animates) */}
        <img 
          src={TURNTABLE_IMAGES.needle} 
          alt="Needle" 
          className={`tonearm ${getNeedleClass()}`}
          style={needleStyle}
        />
      </div>

      {/* Track info overlay */}
      {currentTrack && (
        <div className="track-info-overlay">
          <div className="track-artist">{currentTrack.album?.artist || currentTrack.artist || 'Unknown Artist'}</div>
          <div className="track-album">{currentTrack.album?.album || currentTrack.album || 'Unknown Album'}</div>
          <div className="track-title">{currentTrack.DisplayTitle || currentTrack.title || 'Unknown Track'}</div>
        </div>
      )}

      {/* Control Bar */}
      <div className="turntable-controls" style={controlsStyle}>
        {/* Restart */}
        <button 
          className="control-btn"
          onClick={handleRestart}
          title="Restart Track"
        >
          <span className="material-symbols-outlined">replay</span>
        </button>

        {/* Previous Track */}
        <button 
          className="control-btn"
          onClick={onPrevious}
          disabled={currentIndex <= 0}
          title="Previous Track"
        >
          <span className="material-symbols-outlined">skip_previous</span>
        </button>

        {/* Skip Backward 10s */}
        <button 
          className="control-btn"
          onClick={onSkipBackward}
          title="Back 10 seconds"
        >
          <span className="material-symbols-outlined">replay_10</span>
        </button>

        {/* Play/Pause */}
        <button 
          className={`control-btn play-pause-btn ${isPlaying ? 'playing' : ''}`}
          onClick={onPlayPause}
          title={isPlaying ? 'Pause' : 'Play'}
        >
          <span className="material-symbols-outlined">
            {isPlaying ? 'pause_circle' : 'play_circle'}
          </span>
        </button>

        {/* Skip Forward 10s */}
        <button 
          className="control-btn"
          onClick={onSkipForward}
          title="Forward 10 seconds"
        >
          <span className="material-symbols-outlined">forward_10</span>
        </button>

        {/* Next Track */}
        <button 
          className="control-btn"
          onClick={onNext}
          disabled={currentIndex >= queueLength - 1}
          title="Next Track"
        >
          <span className="material-symbols-outlined">skip_next</span>
        </button>
      </div>

      {/* Queue info */}
      {queueLength > 0 && (
        <div className="queue-info">
          Track {currentIndex + 1} of {queueLength}
        </div>
      )}
    </div>
  );
};

export default Turntable;

